import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, Play, Plus, Sparkles, Trash2, Variable, X } from 'lucide-react';
import type { ManualVariable } from '../triggerWorkflowTypes';
import { postWorkflowRunStart, workflowRunStartErrorMessage } from '../workflowRunStartService';
import { parseTriggerInputsForTestRun, type ParsedTriggerForTestRun } from './workflowTestRunFromDefinition';

/** Load array variable as a list of strings for simple add-by-text UI. */
function stringItemsFromJsonArray(jsonStr: string): string[] {
  try {
    const p = JSON.parse(jsonStr) as unknown;
    if (!Array.isArray(p)) return [];
    return p.map((el: unknown) => {
      if (el === null || el === undefined) return '';
      if (typeof el === 'string') return el;
      if (typeof el === 'number' || typeof el === 'boolean') return String(el);
      try {
        return JSON.stringify(el);
      } catch {
        return String(el);
      }
    });
  } catch {
    return [];
  }
}

export type WorkflowTestRunPayload = {
  triggerLabel?: string;
  triggerVariables: Record<string, unknown>;
};

type WorkflowTestRunDialogProps = {
  open: boolean;
  onClose: () => void;
  getDefinitionJson: () => string;
  /** Opened workflow id (route / saved). Required to call the run engine. */
  workflowId: string | null | undefined;
  onRunTest?: (payload: WorkflowTestRunPayload) => void;
};

function normalizeType(t: string): string {
  const s = t.toLowerCase().trim();
  if (s === 'bool' || s === 'boolean') return 'boolean';
  if (s === 'int' || s === 'integer' || s === 'float' || s === 'double' || s === 'number') return 'number';
  if (s === 'object' || s === 'obj') return 'object';
  if (s === 'array' || s === 'list') return 'array';
  return 'string';
}

function buildInitialFormValues(variables: ManualVariable[]): Record<string, string> {
  const next: Record<string, string> = {};
  for (const v of variables) {
    const t = normalizeType(v.type);
    if (t === 'boolean') next[v.name] = 'false';
    else if (t === 'number') next[v.name] = '';
    else if (t === 'object') next[v.name] = '{}';
    else if (t === 'array') next[v.name] = '[]';
    else next[v.name] = '';
  }
  return next;
}

function parseValue(name: string, typeRaw: string, raw: string): { ok: true; value: unknown } | { ok: false; message: string } {
  const t = normalizeType(typeRaw);
  if (t === 'boolean') {
    const v = raw === 'true';
    return { ok: true, value: v };
  }
  if (t === 'number') {
    const trimmed = raw.trim();
    if (trimmed === '') return { ok: false, message: `${name}: enter a number or use 0.` };
    const n = Number(trimmed);
    if (Number.isNaN(n)) return { ok: false, message: `${name}: invalid number.` };
    return { ok: true, value: n };
  }
  if (t === 'object') {
    const trimmed = raw.trim();
    if (!trimmed) return { ok: false, message: `${name}: JSON cannot be empty.` };
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { ok: false, message: `${name}: expected a JSON object.` };
      }
      return { ok: true, value: parsed };
    } catch {
      return { ok: false, message: `${name}: invalid JSON.` };
    }
  }
  return { ok: true, value: raw };
}

function statusMessage(parsed: ParsedTriggerForTestRun): string | null {
  switch (parsed.status) {
    case 'no_definition':
      return 'No workflow definition is available yet.';
    case 'parse_error':
      return 'Could not read the workflow definition.';
    case 'no_trigger':
      return 'No trigger (start) node was found on the canvas.';
    default:
      return null;
  }
}

const WorkflowTestRunDialog: React.FC<WorkflowTestRunDialogProps> = ({
  open,
  onClose,
  getDefinitionJson,
  workflowId,
  onRunTest,
}) => {
  const [parsed, setParsed] = useState<ParsedTriggerForTestRun | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  /** Array-typed variables: list of string items built from text box + Add. */
  const [arrayStringItems, setArrayStringItems] = useState<Record<string, string[]>>({});
  const [arrayAddDraft, setArrayAddDraft] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [startingRun, setStartingRun] = useState(false);

  const refreshParsed = useCallback(() => {
    const raw = getDefinitionJson();
    const next = parseTriggerInputsForTestRun(raw);
    setParsed(next);
    if (next.status === 'ok') {
      const initial = buildInitialFormValues(next.variables);
      setForm(initial);
      const lists: Record<string, string[]> = {};
      for (const vv of next.variables) {
        if (normalizeType(vv.type) === 'array') {
          lists[vv.name] = stringItemsFromJsonArray(initial[vv.name] ?? '[]');
        }
      }
      setArrayStringItems(lists);
      setArrayAddDraft({});
    } else {
      setForm({});
      setArrayStringItems({});
      setArrayAddDraft({});
    }
    setSubmitError(null);
  }, [getDefinitionJson]);

  useEffect(() => {
    if (!open) return;
    refreshParsed();
  }, [open, refreshParsed]);

  const variables = parsed?.status === 'ok' ? parsed.variables : [];

  const blockingError = parsed ? statusMessage(parsed) : null;
  const missingWorkflowId = !workflowId?.trim();

  const handleField = useCallback((name: string, value: string) => {
    setForm((f) => ({ ...f, [name]: value }));
    setSubmitError(null);
  }, []);

  const runTest = useCallback(async () => {
    setSubmitError(null);
    if (!parsed) return;
    if (parsed.status === 'no_definition' || parsed.status === 'parse_error' || parsed.status === 'no_trigger') {
      return;
    }

    const wid = workflowId?.trim();
    if (!wid) {
      setSubmitError('Open or save this workflow so it has an id before running a test.');
      return;
    }

    const triggerVariables: Record<string, unknown> = {};
    if (parsed.status === 'ok' && parsed.variables.length > 0) {
      const seen = new Set<string>();
      for (const v of parsed.variables) {
        if (seen.has(v.name)) {
          setSubmitError(`Duplicate variable name "${v.name}". Fix the trigger configuration.`);
          return;
        }
        seen.add(v.name);
        if (normalizeType(v.type) === 'array') {
          triggerVariables[v.name] = [...(arrayStringItems[v.name] ?? [])];
        } else {
          const raw = form[v.name] ?? '';
          const r = parseValue(v.name, v.type, raw);
          if (!r.ok) {
            setSubmitError(r.message);
            return;
          }
          triggerVariables[v.name] = r.value;
        }
      }
    }

    const payload: WorkflowTestRunPayload = {
      triggerLabel: parsed.status === 'ok' || parsed.status === 'not_manual' ? parsed.triggerLabel : undefined,
      triggerVariables,
    };

    setStartingRun(true);
    try {
      await postWorkflowRunStart(wid, triggerVariables);
      onRunTest?.(payload);
      onClose();
    } catch (e) {
      setSubmitError(workflowRunStartErrorMessage(e));
    } finally {
      setStartingRun(false);
    }
  }, [parsed, form, arrayStringItems, workflowId, onRunTest, onClose]);

  const appendArrayStringItem = useCallback((fieldName: string, raw: string) => {
    const piece = raw.replace(/\r\n/g, '\n');
    if (!piece.trim()) return;
    setArrayStringItems((prev) => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] ?? []), piece],
    }));
    setArrayAddDraft((d) => ({ ...d, [fieldName]: '' }));
    setSubmitError(null);
  }, []);

  const removeArrayStringItem = useCallback((fieldName: string, index: number) => {
    setArrayStringItems((prev) => ({
      ...prev,
      [fieldName]: (prev[fieldName] ?? []).filter((_, i) => i !== index),
    }));
    setSubmitError(null);
  }, []);

  const subtitle = useMemo(() => {
    if (!parsed) return 'Loading…';
    if (blockingError) return blockingError;
    if (parsed.status === 'not_manual') {
      return `Start node is a ${parsed.triggerKind} trigger. There are no manual input fields; you can still send an empty test payload.`;
    }
    if (parsed.status === 'ok' && variables.length === 0) {
      return 'Manual trigger has no input variables. Test run will use an empty variable map.';
    }
    return 'Values are passed as trigger.variables when you connect an execution API.';
  }, [parsed, blockingError, variables.length]);

  if (!open) return null;

  const showTriggerBadge =
    parsed &&
    !blockingError &&
    (parsed.status === 'ok' || parsed.status === 'not_manual') &&
    Boolean(parsed.triggerLabel?.trim());

  return (
    <div
      className="sidebar-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="sidebar-modal sidebar-modal--test-run"
        role="dialog"
        aria-modal="true"
        aria-labelledby="workflow-test-run-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sidebar-test-run-accent" aria-hidden />
        <button type="button" className="sidebar-test-run-close" onClick={onClose} aria-label="Close test run dialog">
          <X size={17} strokeWidth={2} />
        </button>

        <div className="sidebar-test-run-inner">
          <div className="sidebar-test-run-head">
            <span className="sidebar-test-run-icon-wrap" aria-hidden>
              <Play size={18} strokeWidth={2} className="sidebar-test-run-head-ico" />
            </span>
            <div className="sidebar-test-run-head-text">
              <span className="sidebar-test-run-eyebrow">Workflow test</span>
              <h3 id="workflow-test-run-title" className="sidebar-test-run-title">
                Run with inputs
              </h3>
              {showTriggerBadge ? (
                <div className="sidebar-test-run-meta">
                  <span className="sidebar-test-run-badge" title={parsed.triggerLabel}>
                    <Variable size={13} strokeWidth={2} aria-hidden />
                    <span className="sidebar-test-run-badge-label">{parsed.triggerLabel}</span>
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          {blockingError ? (
            <div className="sidebar-test-run-empty sidebar-test-run-empty--error">
              <AlertCircle size={20} strokeWidth={2} className="sidebar-test-run-empty-ico" aria-hidden />
              <p className="sidebar-test-run-empty-text">{blockingError}</p>
            </div>
          ) : (
            <div className="sidebar-test-run-sub-wrap">
              <p className="sidebar-test-run-sub">{subtitle}</p>
            </div>
          )}

          {!blockingError && parsed?.status === 'ok' && variables.length > 0 ? (
            <div className="sidebar-test-run-fields" role="group" aria-label="Trigger input variables">
              {variables.map((v) => {
                const t = normalizeType(v.type);
                const id = `test-run-var-${v.name}`;
                return (
                  <div key={v.name} className="sidebar-test-run-field-card">
                    <div className="sidebar-test-run-field-head">
                      <div className="sidebar-test-run-name-row">
                        <Variable size={14} strokeWidth={2} className="sidebar-test-run-label-ico" aria-hidden />
                        <span className="sidebar-test-run-label-text" title={v.name}>
                          {v.name}
                        </span>
                      </div>
                      <span className="sidebar-test-run-type-pill">{v.type}</span>
                    </div>
                    {t === 'boolean' ? (
                      <div className="sidebar-test-run-toggle-wrap">
                        <div
                          id={id}
                          className="sidebar-test-run-toggle"
                          role="group"
                          aria-label={`${v.name} value`}
                        >
                          <button
                            type="button"
                            className={`sidebar-test-run-toggle__btn${
                              (form[v.name] ?? 'false') !== 'true' ? ' sidebar-test-run-toggle__btn--on' : ''
                            }`}
                            aria-pressed={(form[v.name] ?? 'false') !== 'true'}
                            onClick={() => handleField(v.name, 'false')}
                          >
                            False
                          </button>
                          <button
                            type="button"
                            className={`sidebar-test-run-toggle__btn${
                              (form[v.name] ?? 'false') === 'true' ? ' sidebar-test-run-toggle__btn--on' : ''
                            }`}
                            aria-pressed={(form[v.name] ?? 'false') === 'true'}
                            onClick={() => handleField(v.name, 'true')}
                          >
                            True
                          </button>
                        </div>
                      </div>
                    ) : t === 'number' ? (
                      <input
                        id={id}
                        className="sidebar-test-run-input"
                        type="number"
                        step="any"
                        value={form[v.name] ?? ''}
                        onChange={(e) => handleField(v.name, e.target.value)}
                        placeholder="Numeric value"
                      />
                    ) : t === 'array' ? (
                      <div className="sidebar-test-run-array-simple" role="group" aria-label={`${v.name} list items`}>
                        <ul className="sidebar-test-run-array-simple-list">
                          {(arrayStringItems[v.name] ?? []).map((item, idx) => (
                            <li key={`${v.name}-${idx}`} className="sidebar-test-run-array-simple-item">
                              <span className="sidebar-test-run-array-simple-text" title={item}>
                                {item}
                              </span>
                              <button
                                type="button"
                                className="sidebar-test-run-array-simple-remove"
                                onClick={() => removeArrayStringItem(v.name, idx)}
                                aria-label={`Remove item ${idx + 1}`}
                              >
                                <Trash2 size={14} strokeWidth={2} aria-hidden />
                              </button>
                            </li>
                          ))}
                        </ul>
                        <div className="sidebar-test-run-array-simple-add">
                          <label className="sidebar-test-run-sr-only" htmlFor={`${id}-array-add`}>
                            Text for next list item
                          </label>
                          <input
                            id={`${id}-array-add`}
                            className="sidebar-test-run-input sidebar-test-run-input--grow"
                            type="text"
                            value={arrayAddDraft[v.name] ?? ''}
                            onChange={(e) => setArrayAddDraft((d) => ({ ...d, [v.name]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                appendArrayStringItem(v.name, arrayAddDraft[v.name] ?? '');
                              }
                            }}
                            placeholder="Type a value, then Add or press Enter"
                          />
                          <button
                            type="button"
                            className="sidebar-test-run-array-add-btn"
                            onClick={() => appendArrayStringItem(v.name, arrayAddDraft[v.name] ?? '')}
                          >
                            <Plus size={15} strokeWidth={2} aria-hidden />
                            Add
                          </button>
                        </div>
                      </div>
                    ) : t === 'object' ? (
                      <textarea
                        id={id}
                        className="sidebar-test-run-textarea"
                        value={form[v.name] ?? ''}
                        onChange={(e) => handleField(v.name, e.target.value)}
                        rows={5}
                        spellCheck={false}
                        placeholder={'{"key": "value"}'}
                      />
                    ) : (
                      <input
                        id={id}
                        className="sidebar-test-run-input"
                        type="text"
                        value={form[v.name] ?? ''}
                        onChange={(e) => handleField(v.name, e.target.value)}
                        placeholder="Text value"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : !blockingError ? (
            <div className="sidebar-test-run-empty">
              <Sparkles size={20} strokeWidth={2} className="sidebar-test-run-empty-ico" aria-hidden />
              <p className="sidebar-test-run-empty-text">
                {parsed?.status === 'not_manual'
                  ? 'This trigger has no manual fields. Run will send an empty variable map unless your backend adds context.'
                  : 'No variables are defined on the manual trigger. Run will send an empty map.'}
              </p>
            </div>
          ) : null}

          {missingWorkflowId && !blockingError ? (
            <p className="sidebar-test-run-submit-error" role="status">
              Save or open a workflow from the list so it has an id before running a test.
            </p>
          ) : null}

          {submitError ? (
            <p className="sidebar-test-run-submit-error" role="alert">
              {submitError}
            </p>
          ) : null}
        </div>

        <div className="sidebar-test-run-footer">
          <div className="sidebar-test-run-actions">
            <button type="button" className="sidebar-modal-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="sidebar-modal-btn sidebar-modal-btn--primary sidebar-test-run-run"
              onClick={() => void runTest()}
              disabled={Boolean(blockingError) || missingWorkflowId || startingRun}
            >
              {startingRun ? (
                <Loader2 size={15} strokeWidth={2} className="sidebar-test-run-run-spinner" aria-hidden />
              ) : (
                <Play size={15} strokeWidth={2} aria-hidden />
              )}
              {startingRun ? 'Starting…' : 'Run test'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowTestRunDialog;
